import { adminAccessPermissions, hasAnyPermission } from '@platform/auth/permissions';

/**
 * Routes, labels and permissions below mirror what App.tsx already registers — this file
 * groups the existing admin surface, it does not add pages or rename anything.
 */
export type AdminNavLink = {
  icon: string;
  route: string;
  label: string;
  requiredPermissions: readonly string[];
  /** Every group must match — for links needing two separate permissions. */
  requiredAllPermissions?: readonly (readonly string[])[];
  /** Hidden unless this flag is globally enabled. */
  featureFlag?: string;
};

export type AdminNavGroup = {
  key: string;
  label: string;
  links: readonly AdminNavLink[];
};

export const adminHomeLink: AdminNavLink = {
  icon: 'home',
  route: '/admin',
  label: 'Admin Home',
  requiredPermissions: adminAccessPermissions,
};

export const adminNavGroups: readonly AdminNavGroup[] = [
  {
    key: 'people',
    label: 'People',
    links: [
      {
        icon: 'groups',
        route: '/admin/coaches',
        label: 'Coaches',
        requiredPermissions: ['admin:coaches'],
      },
      {
        icon: 'group',
        route: '/admin/members',
        label: 'Members',
        requiredPermissions: ['admin:coaches'],
      },
      {
        icon: 'admin_panel_settings',
        route: '/admin/users',
        label: 'User Management',
        requiredPermissions: ['admin:users'],
      },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    links: [
      {
        // 'event_available' was in the original nav but is not in the icon registry, so it rendered blank.
        icon: 'event',
        route: '/admin/appointments',
        label: 'Appointments',
        requiredPermissions: ['admin:coaches'],
      },
      {
        icon: 'assignment_turned_in',
        route: '/admin/appointments/review',
        label: 'Review Queue',
        requiredPermissions: ['admin:coaches'],
      },
      {
        icon: 'event',
        route: '/admin/calendar-changes',
        label: 'Calendar Changes',
        requiredPermissions: ['admin:coaches'],
      },
      {
        icon: 'support',
        route: '/admin/coach-matching',
        label: 'Coach Matching',
        requiredPermissions: ['admin:coaches'],
        requiredAllPermissions: [['admin:coaches'], ['admin:prompts']],
      },
    ],
  },
  {
    key: 'notifications',
    label: 'Notifications',
    links: [
      {
        icon: 'mail',
        route: '/admin/emails',
        label: 'Sent Emails',
        requiredPermissions: ['admin:coaches'],
      },
      {
        icon: 'visibility',
        route: '/admin/email-previews',
        label: 'Email Preview',
        requiredPermissions: ['admin:coaches'],
      },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    links: [
      {
        icon: 'article',
        route: '/admin/artifacts',
        label: 'Artifacts',
        requiredPermissions: ['admin:coaches'],
      },
      {
        icon: 'insights',
        route: '/admin/transcript-coverage',
        label: 'Transcript Coverage',
        requiredPermissions: ['admin:coaches'],
      },
    ],
  },
  {
    key: 'ai',
    label: 'AI',
    links: [
      {
        icon: 'edit_note',
        route: '/admin/prompts',
        label: 'Prompt Editor',
        requiredPermissions: ['admin:prompts'],
      },
      {
        icon: 'auto_awesome',
        route: '/admin/prompt-tester',
        label: 'Prompt Tester',
        requiredPermissions: ['admin:prompts'],
      },
      {
        icon: 'person_edit',
        route: '/admin/profiles',
        label: 'Profile Editor',
        requiredPermissions: ['admin:profiles'],
      },
      {
        icon: 'assignment',
        route: '/admin/evals',
        label: 'Evals',
        requiredPermissions: ['admin:evals'],
      },
    ],
  },
  {
    key: 'system',
    label: 'System',
    links: [
      {
        icon: 'emoji_flags',
        route: '/admin/feature-flags',
        label: 'Feature Flags',
        requiredPermissions: ['admin:*'],
      },
      {
        icon: 'schedule',
        route: '/admin/cron',
        label: 'Cron Jobs',
        requiredPermissions: ['admin:cron'],
      },
      {
        // 'vpn_key' was in the original nav but is not in the icon registry, so it rendered blank.
        icon: 'lock',
        route: '/admin/internal-apps',
        label: 'Internal Apps',
        requiredPermissions: ['admin:internal-apps'],
      },
      {
        icon: 'list_alt',
        route: '/admin/typeform',
        label: 'Typeform',
        requiredPermissions: ['admin:onboarding'],
      },
    ],
  },
];

type VisibilityContext = {
  permissions: string[];
  roles: string[];
  enabledFlags: readonly string[];
};

export const isAdminLinkVisible = (link: AdminNavLink, ctx: VisibilityContext): boolean => {
  if (link.featureFlag && !ctx.enabledFlags.includes(link.featureFlag)) {
    return false;
  }
  if (link.requiredAllPermissions) {
    return link.requiredAllPermissions.every((group) =>
      hasAnyPermission(ctx.permissions, [...group], ctx.roles)
    );
  }
  return hasAnyPermission(ctx.permissions, [...link.requiredPermissions], ctx.roles);
};

export type VisibleAdminNavGroup = AdminNavGroup & { links: AdminNavLink[] };

export const visibleAdminNavGroups = (ctx: VisibilityContext): VisibleAdminNavGroup[] =>
  adminNavGroups
    .map((group) => ({ ...group, links: group.links.filter((l) => isAdminLinkVisible(l, ctx)) }))
    .filter((group) => group.links.length > 0);

const isPathWithin = (pathname: string, route: string): boolean =>
  pathname === route || pathname.startsWith(`${route}/`);

export type ActiveAdminNav = {
  group: VisibleAdminNavGroup;
  link: AdminNavLink;
};

/**
 * Which link — and therefore which group — the current route belongs to.
 *
 * Longest match wins, because admin routes nest: on /admin/appointments/review both
 * /admin/appointments and /admin/appointments/review match by prefix, and only the
 * deeper one should read as active.
 */
export const findActiveAdminNav = (
  pathname: string,
  groups: readonly VisibleAdminNavGroup[]
): ActiveAdminNav | null => {
  let best: ActiveAdminNav | null = null;

  for (const group of groups) {
    for (const link of group.links) {
      if (!isPathWithin(pathname, link.route)) continue;
      if (!best || link.route.length > best.link.route.length) {
        best = { group, link };
      }
    }
  }

  return best;
};
